import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { groupInactiveFDsByRange, InactiveFD, InactiveRangeGroup } from '../utils/dailyReport';

interface TopPerformer {
  bot_name: string;
  bot_client_name: string;
  d_minus_2: number;
  d_minus_1: number;
  d_today: number;
  total_3_days: number;
  variation_percentage: number;
  trend: 'UP' | 'DOWN' | 'STABLE';
}

interface LowVolumeAlert {
  triggered: boolean;
  todayMorning: number;
  lastWeekMorning: number;
  percentage: number;
  weekday: string;
  lastWeekDate: string;
  threshold: number;
}

interface ConsolidatedReportData {
  droppedFDs: any[];
  hibernatingFDs: any[];
  inactiveFDs: InactiveFD[];
  topPerformers: {
    up: TopPerformer[];
    down: TopPerformer[];
    stable: TopPerformer[];
  };
  lowVolumeAlert: LowVolumeAlert;
  generatedAt: string;
}

// Inline SVG Icons
const Icons = {
  refresh: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16" />
    </svg>
  ),
  calendar: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  trendUp: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  trendDown: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
      <polyline points="17 18 23 18 23 12" />
    </svg>
  ),
  alert: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  sleep: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  ),
  activity: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  clock: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  users: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  chart: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
};

// Styles object for cleaner JSX
const styles = {
  container: {
    padding: '24px',
    maxWidth: '1600px',
    margin: '0 auto',
    minHeight: '100vh',
  } as React.CSSProperties,

  header: {
    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.15) 50%, rgba(236, 72, 153, 0.1) 100%)',
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '32px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    position: 'relative' as const,
    overflow: 'hidden',
  } as React.CSSProperties,

  headerGlow: {
    position: 'absolute' as const,
    top: '-50%',
    right: '-20%',
    width: '400px',
    height: '400px',
    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, transparent 70%)',
    pointerEvents: 'none' as const,
  } as React.CSSProperties,

  card: {
    background: 'linear-gradient(145deg, #1f2937 0%, #111827 100%)',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  } as React.CSSProperties,

  metricCard: {
    background: 'linear-gradient(145deg, #1f2937 0%, #111827 100%)',
    borderRadius: '12px',
    padding: '20px',
    textAlign: 'center' as const,
    border: '1px solid rgba(255, 255, 255, 0.05)',
    transition: 'all 0.3s ease',
    cursor: 'default',
  } as React.CSSProperties,

  sectionTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: 600,
    color: '#e5e7eb',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  } as React.CSSProperties,

  badge: {
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  } as React.CSSProperties,

  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '14px',
  } as React.CSSProperties,

  tableHeader: {
    background: 'rgba(55, 65, 81, 0.5)',
    color: '#9ca3af',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    fontSize: '11px',
    letterSpacing: '0.05em',
  } as React.CSSProperties,

  button: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
  } as React.CSSProperties,
};

function DailyReport() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ConsolidatedReportData | null>(null);
  const [inactiveGroups, setInactiveGroups] = useState<InactiveRangeGroup[]>([]);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['volume', 'critical', 'performers']));
  const [expandedLists, setExpandedLists] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadReportData();
  }, []);

  const loadReportData = async () => {
    try {
      setLoading(true);
      setError(null);
      const reportData = await api.getConsolidatedReport();

      if (reportData) {
        setData(reportData);
        if (reportData.inactiveFDs) {
          const groups = groupInactiveFDsByRange(reportData.inactiveFDs);
          setInactiveGroups(groups);
        }
      } else {
        setError('Nao foi possivel carregar os dados do relatorio');
      }
    } catch (err) {
      console.error('Error loading report data:', err);
      setError('Erro ao carregar dados do relatorio');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getVariationInfo = (percentage: number) => {
    if (percentage < -50) return { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)', status: 'CRITICO', icon: Icons.alert };
    if (percentage < -20) return { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', status: 'ATENCAO', icon: Icons.trendDown };
    if (percentage < 0) return { color: '#eab308', bg: 'rgba(234, 179, 8, 0.15)', status: 'ABAIXO', icon: Icons.trendDown };
    if (percentage > 20) return { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)', status: 'EXCELENTE', icon: Icons.trendUp };
    return { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.15)', status: 'NORMAL', icon: Icons.activity };
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const toggleList = (listId: string) => {
    setExpandedLists(prev => {
      const newSet = new Set(prev);
      if (newSet.has(listId)) {
        newSet.delete(listId);
      } else {
        newSet.add(listId);
      }
      return newSet;
    });
  };

  const isListExpanded = (listId: string) => expandedLists.has(listId);

  // Loading State
  if (loading) {
    return (
      <div style={{ ...styles.container, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '3px solid rgba(59, 130, 246, 0.2)',
          borderTopColor: '#3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <p style={{ color: '#9ca3af', marginTop: '20px', fontSize: '16px' }}>Carregando relatorio...</p>
        <style>{`
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes slideIn { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
          @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }
        `}</style>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div style={{ ...styles.container, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{
          ...styles.card,
          maxWidth: '450px',
          textAlign: 'center',
          border: '1px solid rgba(239, 68, 68, 0.3)',
        }}>
          <div style={{ color: '#ef4444', marginBottom: '16px' }}>{Icons.alert}</div>
          <p style={{ color: '#ef4444', margin: '0 0 20px 0', fontSize: '16px' }}>{error}</p>
          <button onClick={loadReportData} style={styles.button}>
            {Icons.refresh}
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ ...styles.container, textAlign: 'center', paddingTop: '100px' }}>
        <p style={{ color: '#6b7280' }}>Nenhum dado disponivel</p>
      </div>
    );
  }

  const droppedCount = data.droppedFDs?.length || 0;
  const hibernatingCount = data.hibernatingFDs?.length || 0;
  const upCount = data.topPerformers?.up?.length || 0;
  const downCount = data.topPerformers?.down?.length || 0;
  const stableCount = data.topPerformers?.stable?.length || 0;
  const variation = data.lowVolumeAlert ? data.lowVolumeAlert.percentage - 100 : 0;
  const variationInfo = getVariationInfo(variation);

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideIn { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.7; } }
        .metric-card:hover { transform: translateY(-4px); box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4); }
        .table-row:hover { background: rgba(59, 130, 246, 0.1) !important; }
        .performer-card:hover { transform: translateX(4px); }
        .section-toggle { cursor: pointer; user-select: none; }
        .section-toggle:hover { opacity: 0.8; }
        .refresh-btn:hover { transform: scale(1.05); box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4); }
      `}</style>

      {/* Header */}
      <div style={{ ...styles.header, animation: 'fadeIn 0.5s ease-out' }}>
        <div style={styles.headerGlow} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ color: '#3b82f6' }}>{Icons.chart}</div>
            <h1 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              Relatorio Diario
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#9ca3af', fontSize: '14px' }}>
            {Icons.calendar}
            <span>{data.generatedAt ? formatDate(data.generatedAt) : 'N/A'}</span>
          </div>
        </div>
      </div>

      {/* Volume Analysis Card */}
      {data.lowVolumeAlert && (
        <div style={{ ...styles.card, animation: 'fadeIn 0.5s ease-out 0.1s backwards' }}>
          <div
            className="section-toggle"
            onClick={() => toggleSection('volume')}
            style={{ ...styles.sectionTitle, justifyContent: 'space-between' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ color: '#3b82f6' }}>{Icons.activity}</div>
              <span>Analise de Volume (Manha 6h-13h)</span>
            </div>
            <span style={{ color: '#6b7280', fontSize: '20px' }}>
              {expandedSections.has('volume') ? '-' : '+'}
            </span>
          </div>

          {expandedSections.has('volume') && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              {/* Today */}
              <div className="metric-card" style={styles.metricCard}>
                <p style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Hoje</p>
                <p style={{ margin: 0, fontSize: '32px', fontWeight: 700, color: '#e5e7eb' }}>
                  {data.lowVolumeAlert.todayMorning?.toLocaleString('pt-BR') || 0}
                </p>
                <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '12px' }}>interacoes</p>
              </div>

              {/* Last Week */}
              <div className="metric-card" style={styles.metricCard}>
                <p style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {data.lowVolumeAlert.weekday} Passada
                </p>
                <p style={{ margin: 0, fontSize: '32px', fontWeight: 700, color: '#e5e7eb' }}>
                  {data.lowVolumeAlert.lastWeekMorning?.toLocaleString('pt-BR') || 0}
                </p>
                <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '12px' }}>interacoes</p>
              </div>

              {/* Variation */}
              <div className="metric-card" style={{ ...styles.metricCard, background: variationInfo.bg, border: `1px solid ${variationInfo.color}30` }}>
                <p style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Variacao</p>
                <p style={{ margin: 0, fontSize: '32px', fontWeight: 700, color: variationInfo.color }}>
                  {variation >= 0 ? '+' : ''}{variation}%
                </p>
                <p style={{ margin: '4px 0 0 0', color: variationInfo.color, fontSize: '12px' }}>vs. semana passada</p>
              </div>

              {/* Status */}
              <div className="metric-card" style={{ ...styles.metricCard, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ color: variationInfo.color, marginBottom: '8px' }}>{variationInfo.icon}</div>
                <p style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: variationInfo.color }}>
                  {variationInfo.status}
                </p>
              </div>
            </div>
          )}

          {data.lowVolumeAlert.triggered && expandedSections.has('volume') && (
            <div style={{
              marginTop: '20px',
              padding: '16px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              animation: 'pulse 2s ease-in-out infinite',
            }}>
              <div style={{ color: '#ef4444' }}>{Icons.alert}</div>
              <span style={{ color: '#ef4444', fontWeight: 600 }}>ALERTA: Volume abaixo de 50% do esperado!</span>
            </div>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px', animation: 'fadeIn 0.5s ease-out 0.2s backwards' }}>
        {[
          { label: 'FDs com Queda', value: droppedCount, color: '#ef4444', icon: Icons.trendDown, alert: droppedCount > 0 },
          { label: 'FDs Hibernando', value: hibernatingCount, color: '#f59e0b', icon: Icons.sleep, alert: hibernatingCount > 0 },
          { label: 'Em Alta', value: upCount, color: '#22c55e', icon: Icons.trendUp, alert: false },
          { label: 'Em Queda', value: downCount, color: '#ef4444', icon: Icons.trendDown, alert: downCount > 0 },
          { label: 'Estaveis', value: stableCount, color: '#6b7280', icon: Icons.activity, alert: false },
        ].map((metric, idx) => (
          <div
            key={idx}
            className="metric-card"
            style={{
              ...styles.metricCard,
              background: metric.alert ? `linear-gradient(145deg, ${metric.color}15 0%, #111827 100%)` : styles.metricCard.background,
              border: metric.alert ? `1px solid ${metric.color}40` : styles.metricCard.border,
            }}
          >
            <div style={{ color: metric.color, marginBottom: '8px', opacity: 0.8 }}>{metric.icon}</div>
            <p style={{ margin: '0 0 4px 0', color: '#6b7280', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{metric.label}</p>
            <p style={{ margin: 0, fontSize: '36px', fontWeight: 700, color: metric.alert ? metric.color : '#e5e7eb' }}>
              {metric.value}
            </p>
          </div>
        ))}
      </div>

      {/* Inactive FDs by Range */}
      {inactiveGroups.length > 0 && (
        <div style={{ ...styles.card, animation: 'fadeIn 0.5s ease-out 0.3s backwards' }}>
          <div style={styles.sectionTitle}>
            <div style={{ color: '#f59e0b' }}>{Icons.clock}</div>
            <span>FDs Inativos por Faixa de Dias</span>
            <span style={{ ...styles.badge, background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', marginLeft: 'auto' }}>
              {inactiveGroups.reduce((acc, g) => acc + g.items.length, 0)} total
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            {inactiveGroups.map((group, groupIdx) => (
              <div key={group.range.id} style={{
                background: 'rgba(31, 41, 55, 0.5)',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                animation: `slideIn 0.3s ease-out ${groupIdx * 0.1}s backwards`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, fontSize: '14px', color: '#e5e7eb', fontWeight: 600 }}>{group.range.label}</h4>
                  <span style={{
                    ...styles.badge,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    color: 'white',
                  }}>
                    {group.items.length}
                  </span>
                </div>

                <div style={{ maxHeight: isListExpanded(`inactive-${group.range.id}`) ? '400px' : '180px', overflowY: 'auto' }}>
                  {(isListExpanded(`inactive-${group.range.id}`) ? group.items : group.items.slice(0, 8)).map((fd, idx, arr) => (
                    <div key={idx} style={{
                      padding: '10px 0',
                      borderBottom: idx < arr.length - 1 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
                    }}>
                      <div style={{ fontWeight: 500, color: '#e5e7eb', fontSize: '13px', marginBottom: '2px' }}>
                        {fd.bot_name || fd.bot_client_name}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '11px' }}>
                        {fd.last_interaction} {fd.organization_name ? `| ${fd.organization_name}` : ''}
                      </div>
                    </div>
                  ))}
                  {group.items.length > 8 && (
                    <button
                      onClick={() => toggleList(`inactive-${group.range.id}`)}
                      style={{
                        padding: '10px 0',
                        color: '#3b82f6',
                        fontSize: '12px',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        width: '100%',
                        textAlign: 'left',
                      }}
                    >
                      {isListExpanded(`inactive-${group.range.id}`) ? 'Ver menos' : `Ver mais (${group.items.length - 8})`}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Critical Alerts - Dropped FDs */}
      {droppedCount > 0 && (
        <div style={{ ...styles.card, border: '1px solid rgba(239, 68, 68, 0.3)', animation: 'fadeIn 0.5s ease-out 0.4s backwards' }}>
          <div
            className="section-toggle"
            onClick={() => toggleSection('critical')}
            style={{ ...styles.sectionTitle, justifyContent: 'space-between' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ color: '#ef4444' }}>{Icons.alert}</div>
              <span style={{ color: '#ef4444' }}>Alertas Criticos - FDs com Queda</span>
              <span style={{ ...styles.badge, background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' }}>
                {droppedCount}
              </span>
            </div>
            <span style={{ color: '#6b7280', fontSize: '20px' }}>
              {expandedSections.has('critical') ? '-' : '+'}
            </span>
          </div>

          {expandedSections.has('critical') && (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    <th style={{ padding: '14px 16px', textAlign: 'left', borderRadius: '8px 0 0 0' }}>FD</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center' }}>3 dias atras</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center' }}>Ontem</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center' }}>Hoje</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center' }}>Dias Inativo</th>
                    <th style={{ padding: '14px 16px', textAlign: 'left', borderRadius: '0 8px 0 0' }}>Ultima Interacao</th>
                  </tr>
                </thead>
                <tbody>
                  {(isListExpanded('dropped') ? data.droppedFDs : data.droppedFDs.slice(0, 15)).map((fd: any, idx: number) => (
                    <tr
                      key={idx}
                      className="table-row"
                      style={{
                        background: idx % 2 === 0 ? 'transparent' : 'rgba(31, 41, 55, 0.3)',
                        transition: 'background 0.2s ease',
                      }}
                    >
                      <td style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                        <div style={{ fontWeight: 500, color: '#e5e7eb' }}>{fd.bot_name || fd.bot_client_name}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>{fd.bot_client_name}</div>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', color: '#9ca3af' }}>
                        {fd.interactions_3_days_ago || 0}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', color: fd.interactions_yesterday === 0 || fd.interactions_yesterday === '0' ? '#ef4444' : '#9ca3af' }}>
                        {fd.interactions_yesterday || 0}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', color: fd.interactions_today === 0 || fd.interactions_today === '0' ? '#ef4444' : '#9ca3af' }}>
                        {fd.interactions_today || 0}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                        <span style={{
                          ...styles.badge,
                          background: parseInt(fd.days_inactive) >= 3 ? '#ef4444' : parseInt(fd.days_inactive) >= 1 ? '#f59e0b' : '#6b7280',
                          color: 'white',
                        }}>
                          {fd.days_inactive}d
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', color: '#6b7280', fontSize: '13px' }}>
                        {fd.last_interaction}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {droppedCount > 15 && (
                <button
                  onClick={() => toggleList('dropped')}
                  style={{
                    padding: '16px',
                    width: '100%',
                    textAlign: 'center',
                    color: '#3b82f6',
                    fontSize: '14px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {isListExpanded('dropped') ? 'Ver menos' : `Ver mais (${droppedCount - 15} FDs adicionais)`}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Top Performers */}
      {data.topPerformers && (upCount > 0 || downCount > 0) && (
        <div style={{ ...styles.card, animation: 'fadeIn 0.5s ease-out 0.5s backwards' }}>
          <div
            className="section-toggle"
            onClick={() => toggleSection('performers')}
            style={{ ...styles.sectionTitle, justifyContent: 'space-between' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ color: '#8b5cf6' }}>{Icons.users}</div>
              <span>Top Performers - Ultimos 3 Dias</span>
            </div>
            <span style={{ color: '#6b7280', fontSize: '20px' }}>
              {expandedSections.has('performers') ? '-' : '+'}
            </span>
          </div>

          {expandedSections.has('performers') && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px' }}>
              {/* Em Alta */}
              {upCount > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <div style={{ color: '#22c55e' }}>{Icons.trendUp}</div>
                    <h4 style={{ margin: 0, color: '#22c55e', fontSize: '14px', fontWeight: 600 }}>Em Alta</h4>
                    <span style={{ ...styles.badge, background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e' }}>{upCount}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: isListExpanded('performers-up') ? '600px' : '350px', overflowY: 'auto' }}>
                    {(isListExpanded('performers-up') ? data.topPerformers.up : data.topPerformers.up.slice(0, 10)).map((fd: TopPerformer, idx: number) => (
                      <div
                        key={idx}
                        className="performer-card"
                        style={{
                          padding: '14px',
                          background: 'rgba(34, 197, 94, 0.05)',
                          borderRadius: '10px',
                          border: '1px solid rgba(34, 197, 94, 0.2)',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 500, color: '#e5e7eb', fontSize: '14px' }}>{fd.bot_name || fd.bot_client_name}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span>{fd.d_minus_2}</span>
                              <span style={{ color: '#4b5563' }}>→</span>
                              <span>{fd.d_minus_1}</span>
                              <span style={{ color: '#4b5563' }}>→</span>
                              <span style={{ color: '#22c55e', fontWeight: 600 }}>{fd.d_today}</span>
                            </div>
                          </div>
                          <span style={{
                            ...styles.badge,
                            background: '#22c55e',
                            color: 'white',
                          }}>
                            +{fd.variation_percentage}%
                          </span>
                        </div>
                      </div>
                    ))}
                    {upCount > 10 && (
                      <button
                        onClick={() => toggleList('performers-up')}
                        style={{
                          padding: '12px',
                          color: '#22c55e',
                          fontSize: '13px',
                          background: 'rgba(34, 197, 94, 0.1)',
                          border: '1px solid rgba(34, 197, 94, 0.2)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        {isListExpanded('performers-up') ? 'Ver menos' : `Ver mais (${upCount - 10})`}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Em Queda */}
              {downCount > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <div style={{ color: '#ef4444' }}>{Icons.trendDown}</div>
                    <h4 style={{ margin: 0, color: '#ef4444', fontSize: '14px', fontWeight: 600 }}>Em Queda</h4>
                    <span style={{ ...styles.badge, background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' }}>{downCount}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: isListExpanded('performers-down') ? '600px' : '350px', overflowY: 'auto' }}>
                    {(isListExpanded('performers-down') ? data.topPerformers.down : data.topPerformers.down.slice(0, 10)).map((fd: TopPerformer, idx: number) => (
                      <div
                        key={idx}
                        className="performer-card"
                        style={{
                          padding: '14px',
                          background: 'rgba(239, 68, 68, 0.05)',
                          borderRadius: '10px',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 500, color: '#e5e7eb', fontSize: '14px' }}>{fd.bot_name || fd.bot_client_name}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span>{fd.d_minus_2}</span>
                              <span style={{ color: '#4b5563' }}>→</span>
                              <span>{fd.d_minus_1}</span>
                              <span style={{ color: '#4b5563' }}>→</span>
                              <span style={{ color: '#ef4444', fontWeight: 600 }}>{fd.d_today}</span>
                            </div>
                          </div>
                          <span style={{
                            ...styles.badge,
                            background: '#ef4444',
                            color: 'white',
                          }}>
                            {fd.variation_percentage}%
                          </span>
                        </div>
                      </div>
                    ))}
                    {downCount > 10 && (
                      <button
                        onClick={() => toggleList('performers-down')}
                        style={{
                          padding: '12px',
                          color: '#ef4444',
                          fontSize: '13px',
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                        }}
                      >
                        {isListExpanded('performers-down') ? 'Ver menos' : `Ver mais (${downCount - 10})`}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Hibernating FDs */}
      {hibernatingCount > 0 && (
        <div style={{ ...styles.card, border: '1px solid rgba(245, 158, 11, 0.3)', animation: 'fadeIn 0.5s ease-out 0.6s backwards' }}>
          <div
            className="section-toggle"
            onClick={() => toggleSection('hibernating')}
            style={{ ...styles.sectionTitle, justifyContent: 'space-between' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ color: '#f59e0b' }}>{Icons.sleep}</div>
              <span style={{ color: '#f59e0b' }}>FDs Hibernando (45+ dias)</span>
              <span style={{ ...styles.badge, background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
                {hibernatingCount}
              </span>
            </div>
            <span style={{ color: '#6b7280', fontSize: '20px' }}>
              {expandedSections.has('hibernating') ? '-' : '+'}
            </span>
          </div>

          {expandedSections.has('hibernating') && (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    <th style={{ padding: '14px 16px', textAlign: 'left', borderRadius: '8px 0 0 0' }}>FD</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center' }}>Dias Inativo</th>
                    <th style={{ padding: '14px 16px', textAlign: 'left' }}>Ultima Interacao</th>
                    <th style={{ padding: '14px 16px', textAlign: 'left', borderRadius: '0 8px 0 0' }}>Categoria</th>
                  </tr>
                </thead>
                <tbody>
                  {(isListExpanded('hibernating') ? data.hibernatingFDs : data.hibernatingFDs.slice(0, 20)).map((fd: any, idx: number) => (
                    <tr
                      key={idx}
                      className="table-row"
                      style={{
                        background: idx % 2 === 0 ? 'transparent' : 'rgba(31, 41, 55, 0.3)',
                        transition: 'background 0.2s ease',
                      }}
                    >
                      <td style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                        <div style={{ fontWeight: 500, color: '#e5e7eb' }}>{fd.bot_name || fd.bot_client_name}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>{fd.bot_client_name}</div>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                        <span style={{
                          ...styles.badge,
                          background: parseInt(fd.days_inactive) >= 100 ? '#ef4444' : '#f59e0b',
                          color: 'white',
                        }}>
                          {fd.days_inactive}d
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', color: '#6b7280', fontSize: '13px' }}>
                        {fd.last_interaction}
                      </td>
                      <td style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', color: '#6b7280', fontSize: '13px' }}>
                        {fd.categoria || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {hibernatingCount > 20 && (
                <button
                  onClick={() => toggleList('hibernating')}
                  style={{
                    padding: '16px',
                    width: '100%',
                    textAlign: 'center',
                    color: '#f59e0b',
                    fontSize: '14px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {isListExpanded('hibernating') ? 'Ver menos' : `Ver mais (${hibernatingCount - 20} FDs adicionais)`}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={{
        textAlign: 'center',
        padding: '32px 20px',
        color: '#6b7280',
        fontSize: '14px',
        animation: 'fadeIn 0.5s ease-out 0.7s backwards',
      }}>
        <button
          className="refresh-btn"
          onClick={loadReportData}
          style={styles.button}
        >
          {Icons.refresh}
          Atualizar Dados
        </button>
        <p style={{ marginTop: '20px', marginBottom: 0 }}>
          Relatorio gerado automaticamente pelo Strategy Dashboard
        </p>
      </div>
    </div>
  );
}

export default DailyReport;
